import { PROJECT_REFRESH_INTERVAL_MS } from "@repo/utils";
import useSWR, { mutate } from "swr";
import { unstable_serialize } from "swr/infinite";

import { type ApiError } from "../ApiError";
import * as fetcher from "../fetcher";
import {
  type CreateOpportunityPayload,
  type CreateOpportunityResponse,
} from "./createOpportunity";
import { getOpportunitiesSearchKeyCreator } from "./search";
import { type StagesEnum } from "./stagesEnum";
import {
  type OpportunitiesSearchFilters,
  type OpportunitiesSearchOptions,
  type Opportunity,
} from "./types";

export const useOpportunity = (id?: number | null, refreshInterval?: number) =>
  useSWR<Opportunity, ApiError>(
    id ? `/opportunities/${id}` : null,
    () => fetcher.opportunity.get(`/${id}`),
    {
      revalidateOnFocus: false,
      revalidateIfStale: false,
      refreshInterval: refreshInterval || PROJECT_REFRESH_INTERVAL_MS,
      dedupingInterval: PROJECT_REFRESH_INTERVAL_MS,
    }
  );

export const saveOpportunity = async (opportunity: Partial<Opportunity>) => {
  if (opportunity) {
    return fetcher.opportunity
      .update(`/${opportunity.id}`, opportunity)
      .then(() => {
        mutate(`/opportunities/${opportunity.id}`);
        mutate(`/opportunities/${opportunity.id}/history`);
        mutate(`/opportunity/${opportunity.id}/nbas`);
      });
  }
};

export const createOpportunity = async (
  opportunity: CreateOpportunityPayload
) => {
  return await fetcher.opportunity.post<CreateOpportunityResponse, false>(
    "/opportunity",
    opportunity
  );
};

export interface MultiupdatePayload {
  coworkerId?: string;
  teamId?: string;
  customerMeetingPointBuCode?: string;
  status?: string;
  statusChangeDetails?: {
    reason?: string;
    description?: string;
  };
}

export const useMultiupdateOpportunity = (
  filters: OpportunitiesSearchFilters,
  options: OpportunitiesSearchOptions
) => {
  const url = "/opportunities/multiupdate";
  return async (data: MultiupdatePayload, opportunityIds: number[]) => {
    const payload = opportunityIds.map((id) => ({
      id,
      ...data,
    }));
    const res = await fetcher.core.update(url, payload);
    mutate(
      unstable_serialize(getOpportunitiesSearchKeyCreator(filters, options))
    );

    return res;
  };
};

export const updateOpportunityStage = async (
  opportunityId: number,
  stage: StagesEnum
) => {
  return fetcher.opportunity
    .update(`/${opportunityId}/stage`, { stage })
    .then(() => {
      mutate(`/opportunities/${opportunityId}`);
    });
};
